import React, { useState, useEffect } from 'react';
import { Clock, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import subscriptionService from '../services/subscriptionService';

const TrialCountdownBanner = () => {
  const navigate = useNavigate();
  const [trialStatus, setTrialStatus] = useState({ inTrial: false, daysRemaining: 0, trialEnd: null });
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTrialStatus();
    // Check if banner was dismissed today
    const dismissedDate = localStorage.getItem('trialBannerDismissed');
    const today = new Date()?.toDateString();
    if (dismissedDate === today) {
      setDismissed(true);
    }
  }, []);

  const loadTrialStatus = async () => {
    setLoading(true);
    const trialInfo = await subscriptionService?.isUserInTrial();
    setTrialStatus(trialInfo);
    setLoading(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal for today only
    localStorage.setItem('trialBannerDismissed', new Date()?.toDateString());
  };

  const handleUpgrade = () => {
    navigate('/subscription-management');
  };

  // Don't show if loading, dismissed, or not in trial
  if (loading || dismissed || !trialStatus?.inTrial) {
    return null;
  }

  // Show urgent warning if 3 days or less
  const isUrgent = trialStatus?.daysRemaining <= 3;

  return (
    <div className={`${
      isUrgent 
        ? 'bg-gradient-to-r from-orange-500 to-red-500' :'bg-gradient-to-r from-blue-600 to-blue-700'
    } text-white shadow-md`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Trial countdown */}
          <div className="flex items-center gap-3">
            <div className={`${
              isUrgent ? 'bg-white bg-opacity-30' : 'bg-white bg-opacity-20'
            } rounded-full p-2`}>
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-sm sm:text-base">
                {isUrgent ? '⚠️ Trial ending soon!' : 'Free trial active'}
              </p>
              <p className="text-xs sm:text-sm opacity-90">
                {trialStatus?.daysRemaining} day{trialStatus?.daysRemaining !== 1 ? 's' : ''} remaining • Upgrade to keep your data
              </p>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleUpgrade}
              className="bg-white text-blue-600 hover:bg-gray-100 px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <span className="hidden sm:inline">View Plans</span>
              <span className="sm:hidden">Upgrade</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleDismiss}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialCountdownBanner;